import { getLogger } from "@logtape/logtape";

type LogLevel = "debug" | "info" | "error";
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  //space for more levels
  error: 5,
};

const DEFAULT_LOG_LEVEL: LogLevel = "error";

const isLogLevel = (value: unknown): value is LogLevel =>
  typeof value === "string" && value in LOG_LEVELS;

// On the server → use LOG_LEVEL. In the browser → use NEXT_PUBLIC_LOG_LEVEL
const envLogLevel =
  typeof window === "undefined"
    ? process.env.LOG_LEVEL
    : process.env.NEXT_PUBLIC_LOG_LEVEL;
const CURRENT_LOG_LEVEL: LogLevel = isLogLevel(envLogLevel)
  ? envLogLevel
  : DEFAULT_LOG_LEVEL;

const appLogger = getLogger(["app"]);
const eventLogger = getLogger(["app", "event"]);

type LogTarget = {
  /** Restrict to these NEXT_PUBLIC_ENV values, e.g. ["demo"]. */
  envs?: string[];
  /** Restrict to these NEXT_PUBLIC_CUSTOMER values, e.g. ["Demo"]. */
  customers?: string[];
};

type LogOpts = {
  force?: boolean;
  /** Only emit when the running env/customer matches. Omitted dimensions are unrestricted. */
  only?: LogTarget;
};

const matchesOnly = (only?: LogTarget): boolean => {
  if (!only) return true;
  if (only.envs && !only.envs.includes(process.env.NEXT_PUBLIC_ENV ?? "")) return false;
  if (
    only.customers &&
    !only.customers.includes(process.env.NEXT_PUBLIC_CUSTOMER ?? "")
  )
    return false;
  return true;
};

const emit = (level: LogLevel, msg: string, data?: unknown, opts?: LogOpts) => {
  if (!matchesOnly(opts?.only)) return;
  if (!opts?.force && LOG_LEVELS[level] < LOG_LEVELS[CURRENT_LOG_LEVEL]) return;

  const properties =
    data !== null && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : undefined;
  switch (level) {
    case "debug":
      appLogger.debug(msg, properties);
      break;
    case "info":
      appLogger.info(msg, properties);
      break;
    case "error":
      appLogger.error(msg, properties);
      break;
  }
};

/**
 * Emit a single-line JSON event, e.g. for a CloudWatch metric filter/alarm
 * keyed on `event`. Bypasses LOG_LEVEL - only `only` can suppress it.
 */
const event = (name: string, data?: Record<string, unknown>, opts?: LogOpts) => {
  if (!matchesOnly(opts?.only)) return;
  eventLogger.error({ event: name, ...data });
};

type LogFunc = (msg: string, data?: unknown, opts?: LogOpts) => void;
type EventFunc = (
  name: string,
  data?: Record<string, unknown>,
  opts?: LogOpts,
) => void;

type Logger = Record<LogLevel, LogFunc> & { event: EventFunc };

/**
 * Split a caught error into log properties. Keeps `stack` as its own field so
 * the trace survives into production logs; `String(error)` would drop it.
 */
const errorToLogObj = (error: unknown): { message: string; stack?: string } => {
  if (!(error instanceof Error)) return { message: String(error) };
  return { message: error.message, stack: error.stack };
};

const log: Logger = {
  debug: (msg, data, opts) => emit("debug", msg, data, opts),
  info: (msg, data, opts) => emit("info", msg, data, opts),
  error: (msg, data, opts) => emit("error", msg, data, opts),
  event,
};

export { log, errorToLogObj };
